import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useParams, useHistory } from 'react-router-dom';
import { Button, Form, Table } from 'react-bootstrap';

const DepositTicketEdit = () => {
    const { id } = useParams(); // Get the ID from the URL
    const history = useHistory();
    const [checks, setChecks] = useState([]);
    const [checksTotal, setChecksTotal] = useState(0.0); // Default to a number
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [formData, setFormData] = useState({}); // For editable fields

    useEffect(() => {
        const fetchTicketDetails = async () => {
            try {
                const response = await api.get(`/api/depositTickets/${id}`);
                setFormData(response.data);

                // Fetch checks within the range
                const { start_range, end_range } = response.data;
                const checksResponse = await api.get('/api/checkreceipts', {
                    params: { startDate: start_range, endDate: end_range },
                });

                setChecks(checksResponse.data.data || []);
                setChecksTotal(parseFloat(checksResponse.data.totalDonation) || 0.0);
            } catch (error) {
                setErrorMessage(
                    error.response?.data?.error || 'Failed to fetch deposit ticket details.'
                );
            } finally {
                setLoading(false);
            }
        };

        fetchTicketDetails();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/api/depositTickets/${id}`, formData);
            history.push(`/viewDepositTicket/${id}`);
        } catch (error) {
            setErrorMessage(
                error.response?.data?.error || 'Failed to update deposit ticket details.'
            );
        }
    };

    const handleGoBack = () => {
        history.goBack();
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (errorMessage) {
        return <div className="alert alert-danger">{errorMessage}</div>;
    }

    const { start_range, end_range, ...otherDetails } = formData;

    // Filter keys starting with 'deno_'
    const denominations = Object.entries(otherDetails).filter(([key]) =>
        key.startsWith('deno_')
    );

    const calculateDenominationsTotal = () => {
        return denominations.reduce((total, [key, count]) => {
            const denominationValue = parseFloat(key.replace('deno_', '')) || 0;
            const countValue = parseInt(formData[key], 10) || 0;
            return total + denominationValue * countValue;
        }, 0);
    };

    // Grand Total Calculation
    const grandTotal = calculateDenominationsTotal() + checksTotal;

    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-end">
                <Button type="submit" style={{ marginRight: '8px' }} form="editForm" variant="primary" className="small-font">
                    Save Changes
                </Button>
                <Button variant="secondary" onClick={handleGoBack} className="me-2 small-font">
                    Go Back
                </Button>
            </div>

            <h3 className="mt-4">Edit Deposit Ticket</h3>

            <Form id="editForm" onSubmit={handleSubmit} className="mt-4">
                <Form.Group className="mb-3">
                    <Form.Label>Start Range</Form.Label>
                    <Form.Control
                        type="date"
                        name="start_range"
                        value={start_range || ''}
                        onChange={handleChange}
                        required
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>End Range</Form.Label>
                    <Form.Control
                        type="date"
                        name="end_range"
                        value={end_range || ''}
                        onChange={handleChange}
                        required
                    />
                </Form.Group>

                <h4 className="mt-4 mb-3">Currency Denominations</h4>
                <Table striped bordered>
                    <thead>
                        <tr>
                            <th>Denomination</th>
                            <th>Count</th>
                            <th>Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {denominations.map(([key, count], index) => {
                            const denominationValue = key.replace('deno_', '');
                            const subtotal = count * denominationValue;
                            return (
                                <tr key={index}>
                                    <td>{denominationValue} x </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            name={key}
                                            value={count || ''}
                                            onChange={handleChange}
                                            min="0"
                                        />
                                    </td>
                                    <td>{subtotal.toFixed(2)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                {/* Currency Total */}
                <div className="row total-row mt-4">
                    <div className="col-label">
                        <h5 className="mb-0">Currency Total:</h5>
                    </div>
                    <div className="col-value">
                        <input
                            type="text"
                            className="form-control font-weight-bold text-end"
                            readOnly
                            value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(calculateDenominationsTotal() || 0)}
                            style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}
                        />
                    </div>
                </div>
            </Form>

            <h4 className="mt-4 mb-3">Checks</h4>
            <Table striped bordered>
                <thead>
                    <tr>
                        <th>Check Number</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {checks.length > 0 ? (
                        checks.map((check, index) => (
                            <tr key={index}>
                                <td>{check.paymentReference}</td>
                                <td>
                                    {new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                    }).format(check.donationAmt)}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="2" className="text-center">
                                No checks available for the selected date range
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {/* Checks Total */}
            <div className="row total-row mt-4">
                <div className="col-label">
                    <h5 className="mb-0">Checks Total:</h5>
                </div>
                <div className="col-value">
                    <input
                        type="text"
                        className="form-control font-weight-bold text-end"
                        readOnly
                        value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(checksTotal || 0)}
                        style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}
                    />
                </div>
            </div>
            {/* Grand Total */}
            <div className="row total-row mt-4">
                <div className="col-label">
                    <h4 className="mb-0">Grand Total:</h4>
                </div>
                <div className="col-value">
                    <input
                        type="text"
                        className="form-control font-weight-bold text-end"
                        readOnly
                        value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(grandTotal || 0)}
                        style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}
                    />
                </div>
            </div>


        </div>
    );
};

export default DepositTicketEdit;